import { AfterViewInit, Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatNoDataRow,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import { TableDataResponse, TablePayload, User } from './models/users';
import { UsersService } from './services/users.service';
import { SelectionModel } from '@angular/cdk/collections';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, catchError, filter, NEVER, of, switchMap, tap } from 'rxjs';
import { UserFilters } from './components/user-filters/user-filters.component';
import { AsyncPipe, DatePipe, NgStyle } from '@angular/common';
import { ExcelExportComponent } from './components/excel-export/excel-export.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { ActionButtonsSkeletonComponent } from './components/action-buttons-skeleton/action-buttons-skeleton.component';
import { TableSkeletonComponent } from './components/table-skeleton/table-skeleton.component';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { UserColumn } from './models/users-columns';
import { MatDialog } from '@angular/material/dialog';
import { UserDetailsComponent } from './components/user-details/user-details.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { openConfirmationDialog } from '../../../../components/confirmation-popup/confirmation-popup';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserService } from '../../../../services/user.service';
import { PageHeaderComponent } from '../../../../components/page-header/page-header.component';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandlerService } from '../../../../utils/cross-xss-script-error-handler-service';

@Component({
  selector: 'app-users',
  imports: [
    UserFilters,
    MatNoDataRow,
    DatePipe,
    ExcelExportComponent,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatCheckbox,
    MatCell,
    MatCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatPaginator,
    ActionButtonsSkeletonComponent,
    TableSkeletonComponent,
    MatSortHeader,
    MatIconButton,
    MatIcon,
    MatTooltip,
    NgStyle,
    MatButton,
    AsyncPipe,
    MatProgressSpinner,
    PageHeaderComponent
  ],
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss'
})
export class UsersComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort) matSort!: MatSort;
  tableData!: TableDataResponse;
  selection = new SelectionModel<User>(true, []);
  columnsToDisplay: UserColumn[] = [
    { key: 'name', label: 'Name', filterable: true },
    { key: 'email', label: 'Email', filterable: true },
    { key: 'mainPartyName', label: 'Party name', filterable: true },
    { key: 'latestLogin', label: 'Last login', filterable: false },
    { key: 'userType', label: 'User type', filterable: false },
    { key: 'portalAccess', label: 'Portal access', filterable: false }
  ];
  columnsToDisplayMap = new Map(this.columnsToDisplay.map(column => [column.key, column.label]));
  displayedColumns: string[] = ['select', ...this.columnsToDisplay.map(col => col.key), 'actions'];
  isLoading = true;
  isTableInitiating = true;
  selectedUsers: User[] = [];
  isUserAdmin$ = of(false);
  allAvailableColumns = [
    {
      key: 'search.value',
      name: 'Search'
    }
  ];

  private usersService = inject(UsersService);
  private destroyRef = inject(DestroyRef);
  private dialog = inject(MatDialog);
  private matSnackBar = inject(MatSnackBar);
  private userService = inject(UserService);
  private errorHandler = inject(ErrorHandlerService);

  pageConfig$ = new BehaviorSubject<Partial<TablePayload>>(this.usersService.defaultConfig);

  ngOnInit() {
    this.isUserAdmin$ = this.userService.isUserAdmin();
    this.pageConfig$
      .pipe(
        switchMap(config => this.usersService.getUsers(config)),
        tap(users => {
          this.tableData = users;
          this.isTableInitiating = false;
          this.isLoading = false;
        }),
        catchError(error => {
          console.error(error);
          this.isLoading = false;
          this.errorHandler.showApiError(
            error,
            'Could not get the data, please try again later',
            this.allAvailableColumns,
            (fieldName, options) => options.find(x => x.key === fieldName)?.name
          );
          return NEVER;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  ngAfterViewInit() {
    this.handleSelectionChange();
  }

  onSort({ active, direction }: Sort) {
    if (direction) {
      this.updatePageConfig({ sort: { columnName: active, direction } });
    } else {
      this.updatePageConfig({ sort: null });
    }
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.tableData.data.length;
    return numSelected === numRows;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }
    this.selection.select(...this.tableData.data);
  }

  handleSelectionChange() {
    this.selection.changed
      .pipe(
        tap(() => (this.selectedUsers = this.selection.selected)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  onPaginationChange({ pageIndex, pageSize }: PageEvent) {
    this.updatePageConfig({ pageSize, pageNumber: pageIndex + 1 });
  }

  onFilterChange(search: { columnName: string | null; value: string } | null) {
    if (!search) {
      this.updatePageConfig({ search, pageNumber: 1, sort: null });
      this.matSort.direction = '';
      this.matSort.active = '';
    } else {
      this.updatePageConfig({ search, pageNumber: 1 });
    }
    this.selectedUsers = [];
    this.selection.clear(true);
  }

  createUser() {
    this.dialog
      .open(UserDetailsComponent, {
        width: '90vw',
        disableClose: true
      })
      .afterClosed()
      .pipe(
        filter(response => response === 'Success'),
        tap(() => this.updatePageConfig({ pageNumber: 1 })),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  editUser({ id }: User) {
    this.dialog
      .open(UserDetailsComponent, {
        width: '90vw',
        disableClose: true,
        data: { id }
      })
      .afterClosed()
      .pipe(
        filter(response => response === 'Success'),
        tap(() => this.updatePageConfig({ pageNumber: 1 })),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  copyUser({ id }: User) {
    this.dialog
      .open(UserDetailsComponent, {
        width: '90vw',
        disableClose: true,
        data: { id: id.toString(), copyMode: true }
      })
      .afterClosed()
      .pipe(
        filter(response => response === 'Success'),
        tap(() => this.updatePageConfig({ pageNumber: 1 })),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  deleteUser({ name, id }: User) {
    openConfirmationDialog(this.dialog, {
      title: 'Delete user',
      message: `Are you sure you want to delete user: ${name}?`
    })
      .afterClosed()
      .pipe(
        filter(result => result === 'Confirmed'),
        switchMap(() => this.usersService.deleteUserBy(id)),
        tap(() => {
          this.updatePageConfig();
          this.matSnackBar.open('The user was deleted successfully', 'x', { panelClass: 'snackbar-green' });
        }),
        catchError(error => {
          console.error(error);
          this.matSnackBar.open('Could not delete the user, please try again', 'x');
          return NEVER;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private updatePageConfig(options?: Partial<TablePayload>) {
    this.isLoading = true;
    this.pageConfig$.next({ ...this.pageConfig$.value, ...options });
  }
}
